// src/oura/screens/TodayList.tsx
//
// Today: every client's next step that needs us, in one list, sorted by the
// same rule every screen uses (a fix that came back, a client reply waiting
// on us, the item due soonest, then the plan's order). A row opens that
// client's Delivery with the deliverable's drawer open.

import { useEffect, useMemo, useRef, useState } from "react";
import { SunIcon, UserPlusIcon } from "lucide-react";
import type { Onboarding } from "@service/aeo/onboarding";
import { nextAction, type NextAction, type OnboardingPlanFacts } from "../onboarding/derive";
import type { TodayPayload, TodayRow } from "@service/aeo/delivery";
import { actorText, getToday, isDeliveryServed, subscribeDelivery } from "@service/aeo/delivery";
import { OURA_ROOT, ouraHref } from "../nav";
import { cn } from "../lib/cn";
import { FamilyChip, FoldCard, FoldScope, GroupFold, ListRow, TYPE } from "../ui";
import { familyOf } from "../components/plan/planModel";

type Load = { state: "loading" } | { state: "failed" } | { state: "ready"; data: TodayPayload };

function rowHref(r: TodayRow): string {
    return `${OURA_ROOT}/businesses/${encodeURIComponent(r.businessId)}/delivery?d=${encodeURIComponent(r.deliverableKey)}`;
}

function Row({ r }: { r: TodayRow }) {
    const who = actorText(r.actor);
    const warn = r.actor.late || r.actor.cameBack;
    return (
        <ListRow
            lead="square"
            icon={<FamilyChip family={familyOf(r.code)}>{r.code}</FamilyChip>}
            title={`${r.businessName} · ${r.label}`}
            sub={<span className="text-gray-900">{r.next.text}</span>}
            meta={warn ? undefined : who}
            late={warn ? who : undefined}
            to={rowHref(r)}
            stackMeta
            data-today={`${r.businessId}:${r.deliverableKey}`}
        />
    );
}

/** An onboarding client whose Next card is ours: the same words the card says. */
interface OnboardingDue {
    businessId: string;
    name: string;
    next: NextAction;
}

/**
 * The onboarding Next cards that need us, most urgent first (late, then the
 * order the cards themselves come in). Waiting on the client or on Oura is not here.
 */
function onboardingDue(records: Record<string, Onboarding>, plans: Record<string, OnboardingPlanFacts>): OnboardingDue[] {
    return Object.values(records)
        .map((o) => ({ businessId: o.businessId, name: o.businessName || o.domain, next: nextAction(o, Date.now(), { plan: plans[o.businessId] ?? null }) }))
        .filter((r) => r.next.who === "staff" && r.next.cta && r.next.kind !== "done")
        .sort((a, b) => Number(b.next.late) - Number(a.next.late));
}

function OnboardingRow({ r }: { r: OnboardingDue }) {
    return (
        <ListRow
            lead="square"
            icon={<UserPlusIcon aria-hidden size={16} className="text-blue-600" />}
            title={`${r.name} · ${r.next.title}`}
            sub={<span className="text-gray-900">{r.next.why}</span>}
            meta={r.next.late ? undefined : r.next.cta ?? undefined}
            late={r.next.late ? r.next.cta ?? "Late" : undefined}
            to={ouraHref("onboarding", { role: "oura-staff", businessId: r.businessId })}
            stackMeta
            data-today={`${r.businessId}:onboarding`}
        />
    );
}

export interface TodayListProps {
    /** The clients being onboarded, as the list read them: their Next cards that need us lead the list. */
    onboarding?: { records: Record<string, Onboarding>; plans: Record<string, OnboardingPlanFacts> };
}

export function TodayList({ onboarding }: TodayListProps = {}) {
    const due = useMemo(() => (onboarding ? onboardingDue(onboarding.records, onboarding.plans) : []), [onboarding]);
    const [load, setLoad] = useState<Load>({ state: "loading" });
    /** Every Delivery write reads the list again; only the newest read lands, so a slow older one never brings back done work. */
    const readSeq = useRef(0);

    useEffect(() => {
        let live = true;
        const read = () => {
            const mine = ++readSeq.current;
            return getToday().then((r) => {
                if (!live || mine !== readSeq.current) return;
                setLoad(r.ok ? { state: "ready", data: r.data } : { state: "failed" });
            });
        };
        void read();
        const off = subscribeDelivery(() => void read());
        return () => {
            live = false;
            off();
        };
    }, []);

    if (!isDeliveryServed && due.length === 0) return <p className={cn(TYPE.tb, "text-gray-600")}>The Today list comes with Delivery, which is not available here yet.</p>;
    // Delivery's own loading and failure belong to Delivery's part: onboarding work is shown whatever it does.
    if (isDeliveryServed && load.state === "loading" && due.length === 0) return <div className="h-48 animate-pulse rounded-2xl bg-white" aria-busy />;
    if (load.state === "failed" && due.length === 0) return <p className={cn(TYPE.tb, "text-gray-600")}>The Today list did not load. Try again in a minute.</p>;

    const rows = load.state === "ready" ? load.data.rows ?? [] : [];
    const notSet = load.state === "ready" ? load.data.notSet ?? [] : [];
    const deliveryFailed = load.state === "failed";
    const dueClients = new Set(due.map((r) => r.businessId));
    // The band the server sends (came back, a client reply, late, or due by today); without one, what the actor shows.
    const bandOf = (r: TodayRow) => r.band ?? (r.actor.cameBack || r.actor.late ? "today" : "coming");
    const today = rows.filter((r) => bandOf(r) === "today");
    const coming = rows.filter((r) => bandOf(r) === "coming");
    const clients = new Set([...rows.map((r) => r.businessId), ...Array.from(dueClients)]).size;
    const needUs = today.length + due.length;

    return (
        <FoldScope value="staff-today">
            <p className={cn(TYPE.ts, "m-0 mb-4")}>
                {rows.length === 0 && due.length === 0
                    ? "Nothing needs us today."
                    : `${needUs} ${needUs === 1 ? "step needs" : "steps need"} us today, across ${clients} ${clients === 1 ? "client" : "clients"}. ${coming.length} more coming up.`}
            </p>
            <FoldCard id="today" title="Today" icon={{ icon: SunIcon, area: "work" }} foldedFact={`${needUs} today · ${coming.length} coming up`}>
                <div>
                    {due.length > 0 && (
                        <GroupFold id="today.onboarding" tone="blue" title="Onboarding" count={due.length} lead="square">
                            {due.map((r) => (
                                <OnboardingRow key={r.businessId} r={r} />
                            ))}
                        </GroupFold>
                    )}
                    {today.length > 0 && (
                        <GroupFold id="today.now" tone="blue" title="Delivery" count={today.length} lead="square">
                            {today.map((r) => (
                                <Row key={`${r.businessId}:${r.deliverableKey}`} r={r} />
                            ))}
                        </GroupFold>
                    )}
                    {coming.length > 0 && (
                        <GroupFold id="today.coming" tone="gray" title="Coming up" count={coming.length} lead="square">
                            {coming.map((r) => (
                                <Row key={`${r.businessId}:${r.deliverableKey}`} r={r} />
                            ))}
                        </GroupFold>
                    )}
                    {rows.length === 0 && due.length === 0 && <p className={cn(TYPE.tb, "m-0 text-gray-600")}>Every client's next step is with the client or waiting on Oura's check.</p>}
                </div>
                {deliveryFailed && (
                    <p role="status" className={cn(TYPE.tc, "m-0 mt-3 text-gray-600")}>
                        Delivery did not load. The onboarding steps above are still current.
                    </p>
                )}
                {notSet.length > 0 && (
                    <p className={cn(TYPE.tc, "m-0 mt-4 text-gray-500")}>
                        No contract in Oura yet, so no Delivery: {notSet.map((n) => n.businessName).join(", ")}.
                    </p>
                )}
            </FoldCard>
        </FoldScope>
    );
}
